/**
 * Envelope seals — authored via the `/pack` toile process (plan
 * `thinking/plans/2026_08_31_toile_pack_wiring.md` Track B), wave `toile_stickers`. Narcis file-split
 * shape (Track A): one module per pack, `registry.ts` assembles them into `livePacks`.
 */
import {tagline, chips, type ToilePack} from './data'

export const envelopeSealsPack: ToilePack = {
    slug: 'envelope-seals',
    category: 'sticker',
    name: 'Envelope seals',
    tagline: tagline('pieces', 'themes', 'styles'),
    chips: chips('themes', 'styles'),
    ratio: {w: 1024, h: 1024},
    readings: [
        {key: 'wax', label: 'Wax seal look', subject: 'A sheet of round seals each drawn as a red wax seal with a blank initial band, no lettering'},
        {key: 'floral', label: 'Floral circle', subject: 'A sheet of round seals each with a wreath border and a blank centre, no lettering'},
        {key: 'thanks', label: 'Thank you', subject: 'A sheet of round seals each lettered "THANK YOU"'},
    ],
    styles: [
        {key: 'kiss', label: 'Kiss-cut sheet', style: 'kiss-cut sticker sheet, glossy stickers on a matte backing, offset white borders, cut lines drawn', palette: 'Mixed bright colours on a pale backing sheet', framing: 'Nine to twelve stickers arranged in rows on one sheet with a margin, a plain background between them'},
        {key: 'die', label: 'Die-cut set', style: 'die-cut sticker set, each shape cut to its own outline, white border', palette: 'One bright flat colour, no gradient', framing: 'The set laid out spaced apart on a plain ground'},
        {key: 'planner', label: 'Planner strip', style: 'planner sticker strip, small tidy icons in one pastel palette, cut lines drawn', palette: 'One soft pastel palette', framing: 'Small stickers in neat rows on a narrow strip with a margin, on a plain background'},
    ],
    directions: ['in gold foil instead', 'for a wedding invitation', 'as a single large seal'],
    directionHint: 'try: in gold foil instead',
    seo: {
        title: 'Envelope Seals: Wax Look, Floral Circle, Thank You',
        metaDescription: 'Nine round envelope seals — a wax-seal look, a floral circle, a lettered thank you — in kiss-cut, die-cut, and planner finishes.',
        h1: 'Envelope seals: wax look, floral circle, and thank you in three finishes',
        tagline: 'Nine round seals, three designs, three finishes each.',
        intro: 'The round seal for the flap: a wax-seal look, a floral circle, a lettered thank you, each with cut lines drawn in. Rendered kiss-cut, die-cut, and planner-strip so the sheet matches how you actually cut and apply them. Ask for a monogram letter or a different phrase, and the same three finishes carry it.',
        useCases: [
            'sealing wedding or party invitations',
            'closing a handwritten letter or card',
            'a small business packaging seal',
            'a scrapbook or journal accent',
            'a printable envelope seal sheet',
        ],
        faq: [
            {q: 'Are these actual wax seals, or printed stickers?', a: 'These are printed sticker designs styled to look like a wax seal — no melted wax or seal stamp needed.'},
            {q: 'Do the cut lines mean I need a special cutter?', a: 'The cut lines guide scissors or a craft cutting machine equally well — no special tool is required.'},
            {q: 'Can I get a monogram letter or my own short phrase?', a: 'Yes — the direction field takes any letter or short phrase, rendered in the same three finishes.'},
            {q: 'What size envelope do these fit?', a: 'The round seal size suits standard invitation and letter envelopes; resize before printing for a smaller card or a larger flap.'},
            {q: "Can I use these for a small business or wedding I'm selling services for?", a: 'Personal and small-business packaging use is fine; check the license terms before reselling the seal designs themselves.'},
        ],
    },
}
